import React from "react";
import styles from "../assets/styles/AbpSainsburysDashboard.module.css";

const mockPortfolio = {
  date: "Friday, 14 November 2025",
  asOf: "06:00",
  farmsReporting: 46,
  farmsTotal: 50,
  tags: ["ABP / Sainsbury's", "FarmTwin Portfolio Pilot", "Daily portfolio view"],
  filters: {
    retailer: "Sainsbury's only",
    species: "Beef + Lamb",
    region: "Shropshire + Mid-Wales",
    scheme: "Baseline + SFI"
  },
  esg: {
    E1: { label: "Nitrogen Efficiency Index", score: 71, delta: 2 },
    E2: { label: "GHG Intensity (kg CO₂e / kg LW)", value: 24.8, baseline: 27.5 },
    E3: {
      label: "Manure & Runoff Risk",
      highRiskFields: 9,
      totalFields: 215,
      slurryLowWeeks: 3
    },
    S4: {
      soilTestsUpToDatePct: 82,
      highPFieldsPct: 14,
      riparianBuffersPct: 61
    },
    G5: {
      farmsCompleteData: 44,
      farmsTotal: 50,
      verifiedPlans: 40,
      mrvCoveragePct: 95
    }
  },
  weather: {
    shropshire: {
      today: "Light rain, 10–13 °C, wind 12 mph",
      rainfall7d: 52,
      trafficability: 35
    },
    midWales: {
      today: "Showers, 8–11 °C, wind 18 mph",
      rainfall7d: 65,
      trafficability: 40,
      spreadingWindowClosedPct: 60
    },
    runoffRiskIndex: 0.68,
    runoffHigh: 6,
    runoffMedium: 18,
    fieldAccessPctGrass: 72,
    fieldAccessPctArable: 61
  },
  markets: {
    beefPrice: 4.88,
    beef7dAvg: 4.85,
    lambPrice: 6.45,
    lamb7dAvg: 6.40,
    feedDeltaPct: 2.1,
    fertiliserDeltaPct: 0.2,
    energyDeltaPct: -3.4,
    marginPressureIndex: 0.54,
    highPressureFarms: 10
  },
  alerts: {
    defra: [
      {
        id: 1,
        title: "River Severn high-P soil guidance",
        type: "Nutrient management",
        impact: "Immediate compliance risk",
        note: "New restrictions for P Index +3 fields near watercourses, effective from 1 Dec 2025."
      },
      {
        id: 2,
        title: "TB movement update – Powys",
        type: "Animal health",
        impact: "Monitoring only",
        note: "Localised movement licences in place; no direct impact on current collection rounds."
      },
      {
        id: 3,
        title: "SFI riparian buffer actions 2026",
        type: "Scheme opportunity",
        impact: "Opportunity – new payment",
        note: "New actions for buffered edges on high-risk fields from 2026 application window."
      }
    ],
    schemeParticipation: {
      sfiFarms: 24,
      prismFarms: 30,
      compliantFarms: 36,
      totalFarms: 50
    }
  },
  dataQuality: {
    farmsSynced24h: 47,
    farmsStale: 3,
    streamsActive: 18,
    streamsTotal: 20,
    mrvConfidence: 0.82
  },
  dailyActions: [
    {
      id: 1,
      audience: "ABP Farm Engagement",
      text: "Engage 3 farms in the Severn catchment with High runoff risk to agree remedial actions on 2 high-P, steep fields."
    },
    {
      id: 2,
      audience: "ABP Farm Engagement",
      text: "Follow up with 3 farms with slurry storage under 10 weeks to confirm contingency for wet conditions."
    },
    {
      id: 3,
      audience: "x10AI / Data",
      text: "Restore full data sync for 3 farms with no FarmTwin updates for more than 7 days."
    },
    {
      id: 4,
      audience: "Sainsbury's ESG",
      text: "Identify 5 candidate farms for SFI buffer-strip actions where repeated nutrient losses are shown in FarmTwin."
    },
    {
      id: 5,
      audience: "ABP Procurement",
      text: "Factor margin pressure scores into short-term scheduling for 10 farms under High margin pressure."
    }
  ]
};

const mockContract = {
  horizons: [
    {
      id: "h7",
      label: "Next 7 days",
      beef: { contracted: 820, forecast: 790 },
      lamb: { contracted: 1650, forecast: 1720 },
      note: "Beef shortfall concentrated in Shropshire grass-finished cohort; lamb surplus from Mid-Wales housed lambs."
    },
    {
      id: "h14",
      label: "8–14 days",
      beef: { contracted: 800, forecast: 815 },
      lamb: { contracted: 1600, forecast: 1570 },
      note: "Lamb slightly short; scope to draw on over-target lambs from week 1."
    },
    {
      id: "h30",
      label: "15–30 days",
      beef: { contracted: 1580, forecast: 1655 },
      lamb: { contracted: 3150, forecast: 3070 },
      note: "Within tolerance overall; beef marginally ahead, lamb marginally behind."
    },
    {
      id: "h60",
      label: "31–60 days",
      beef: { contracted: 3200, forecast: 3260 },
      lamb: { contracted: 6100, forecast: 5920 },
      note: "Forecast relies heavily on assumed DLWG and grass supply; confidence medium."
    }
  ],
  weekly: [
    {
      weekStart: "17 Nov 2025",
      beefContracted: 820,
      beefForecast: 790,
      lambContracted: 1650,
      lambForecast: 1720,
      comment: "Beef shortfall, lamb surplus; adjust kill mix or pull cattle from week 2."
    },
    {
      weekStart: "24 Nov 2025",
      beefContracted: 800,
      beefForecast: 815,
      lambContracted: 1600,
      lambForecast: 1570,
      comment: "Lamb slightly short; scope to advance lambs depending on weights."
    },
    {
      weekStart: "01 Dec 2025",
      beefContracted: 820,
      beefForecast: 830,
      lambContracted: 1650,
      lambForecast: 1610,
      comment: "Within tolerance; monitor DLWG and grade forecasts."
    },
    {
      weekStart: "08 Dec 2025",
      beefContracted: 800,
      beefForecast: 780,
      lambContracted: 1600,
      lambForecast: 1585,
      comment: "Watch beef volumes; wet ground could push ready dates back."
    }
  ],
  drivers: [
    "Wet conditions in Mid-Wales are constraining field access; potential delays to drafting and loading on heavy land.",
    "Below-plan DLWG on three high-volume beef farms linked to restricted grazing days.",
    "Two lamb finishing farms have animals approaching withdrawal-period windows for treatments; may not be draftable in the short term.",
    "Higher concentrate feeding to recover DLWG would increase feed-related emissions and slurry volume on already constrained farms."
  ]
};

const priorities = [
  {
    id: 1,
    level: "high",
    text: "Runoff Risk High in 4 farms (Severn catchment); 2 fields exceed P Index +3."
  },
  {
    id: 2,
    level: "medium",
    text: "New Defra nutrient guidance triggers review of 6 at-risk fields before 1 Dec 2025."
  },
  {
    id: 3,
    level: "medium",
    text: "3 farms with slurry capacity under 10 weeks as rainfall stays above seasonal norms."
  },
  {
    id: 4,
    level: "low",
    text: "Portfolio GHG intensity 10% below baseline; no immediate action required."
  }
];

function PriorityStrip() {
  return (
    <div className={`${styles.priorityStrip} ${styles.screenOnly}`}>
      {priorities.map((p) => (
        <div
          key={p.id}
          className={`${styles.priorityPill} ${
            p.level === "high"
              ? styles.levelHigh
              : p.level === "medium"
              ? styles.levelMedium
              : styles.levelLow
          }`}
        >
          <span className={styles.level} />
          <span>{p.text}</span>
        </div>
      ))}
    </div>
  );
}

function ESGSnapshot({ esg }) {
  const { E1, E2, E3, S4, G5 } = esg;
  const ghgImprovementPct = ((E2.baseline - E2.value) / E2.baseline) * 100;

  return (
    <div className={styles.grid}>
      <div className={styles.card} style={{ gridColumn: "span 4" }}>
        <div className={styles.cardHeader}>
          <div>
            <div className={styles.cardTitle}>Environmental (E1–E3)</div>
            <div className={styles.cardSubtitle}>
              Nitrogen, emissions, and manure / runoff risk
            </div>
          </div>
        <div className={styles.pillMuted}>Portfolio snapshot</div>
        </div>
        <div className={styles.kpiRow}>
          <div className={styles.kpiBlock}>
            <div className={styles.kpiLabel}>{E1.label}</div>
            <div className={styles.kpiValue}>{E1.score}</div>
            <div className={`${styles.kpiDelta} ${(E1.delta < 0 ? styles.negative : "")}`}>
              {E1.delta >= 0 ? "+" : ""}
              {E1.delta} vs 30-day avg
            </div>
          </div>
          <div className={styles.kpiBlock}>
            <div className={styles.kpiLabel}>{E2.label}</div>
            <div className={styles.kpiValue}>{E2.value.toFixed(1)}</div>
            <div className={styles.kpiDelta}>
              {ghgImprovementPct.toFixed(1)}% below baseline
            </div>
            <div className={styles.sparkline} />
          </div>
        </div>
        <ul className={styles.bulletList}>
          <li>
            High-risk fields: <strong>{E3.highRiskFields}</strong> of{" "}
            <strong>{E3.totalFields}</strong>; slurry capacity under 10 weeks on{" "}
            <strong>{E3.slurryLowWeeks}</strong> farms.
          </li>
        </ul>
      </div>

      <div className={styles.card} style={{ gridColumn: "span 4" }}>
        <div className={styles.cardHeader}>
          <div>
            <div className={styles.cardTitle}>Social (S4 – Land & Soils)</div>
            <div className={styles.cardSubtitle}>Soil testing and land risk status</div>
          </div>
          <span className={`${styles.badge} ${styles.amber}`}>Amber</span>
        </div>
        <div className={styles.kpiRow}>
          <div className={styles.kpiBlock}>
            <div className={styles.kpiLabel}>Soil tests up to date</div>
            <div className={styles.kpiValue}>{S4.soilTestsUpToDatePct}%</div>
          </div>
          <div className={styles.kpiBlock}>
            <div className={styles.kpiLabel}>Fields at P Index +3 or higher</div>
            <div className={styles.kpiValue}>{S4.highPFieldsPct}%</div>
          </div>
        </div>
        <ul className={styles.bulletList}>
          <li>
            Riparian buffer strips present on{" "}
            <strong>{S4.riparianBuffersPct}%</strong> of at-risk edges.
          </li>
          <li>Target: reduce high-P fields to under 10% by 2027.</li>
        </ul>
      </div>

      <div className={styles.card} style={{ gridColumn: "span 4" }}>
        <div className={styles.cardHeader}>
          <div>
            <div className={styles.cardTitle}>Governance (G5 – Transparency)</div>
            <div className={styles.cardSubtitle}>Data completeness and MRV coverage</div>
          </div>
          <span className={`${styles.badge} ${styles.green}`}>Amber-Green</span>
        </div>
        <div className={styles.kpiRow}>
          <div className={styles.kpiBlock}>
            <div className={styles.kpiLabel}>Farms with complete data (14 days)</div>
            <div className={styles.kpiValue}>
              {G5.farmsCompleteData}/{G5.farmsTotal}
            </div>
          </div>
          <div className={styles.kpiBlock}>
            <div className={styles.kpiLabel}>Verified nutrient plans</div>
            <div className={styles.kpiValue}>
              {G5.verifiedPlans}/{G5.farmsTotal}
            </div>
          </div>
        </div>
        <ul className={styles.bulletList}>
          <li>
            MRV audit trail covers <strong>{G5.mrvCoveragePct}%</strong> of reported
            emissions and nutrient flows.
          </li>
          <li>Three farms need follow-up on nutrient export records.</li>
        </ul>
      </div>
    </div>
  );
}

function WeatherAndRisk({ weather }) {
  const {
    shropshire,
    midWales,
    runoffRiskIndex,
    runoffHigh,
    runoffMedium,
    fieldAccessPctGrass,
    fieldAccessPctArable
  } = weather;

  return (
    <div className={styles.grid}>
      <div className={styles.card} style={{ gridColumn: "span 6" }}>
        <div className={styles.cardHeader}>
          <div>
            <div className={styles.cardTitle}>Local daily conditions</div>
            <div className={styles.cardSubtitle}>
              Shropshire and Mid-Wales portfolio regions
            </div>
          </div>
          <span className={styles.pillMuted}>Weather-linked risk indices</span>
        </div>
        <div className={styles.kpiRow}>
          <div className={styles.kpiBlock}>
            <div className={styles.kpiLabel}>Shropshire</div>
            <div className={styles.kpiValue} style={{ fontSize: "0.9rem" }}>
              {shropshire.today}
            </div>
            <div className={styles.kpiDelta}>
              7-day rainfall: {shropshire.rainfall7d} mm (above seasonal)
            </div>
          </div>
          <div className={styles.kpiBlock}>
            <div className={styles.kpiLabel}>Mid-Wales</div>
            <div className={styles.kpiValue} style={{ fontSize: "0.9rem" }}>
              {midWales.today}
            </div>
            <div className={styles.kpiDelta}>
              7-day rainfall: {midWales.rainfall7d} mm (above seasonal)
            </div>
          </div>
        </div>
        <ul className={styles.bulletList}>
          <li>
            Ground trafficability restricted on{" "}
            <strong>{shropshire.trafficability}%</strong> of Shropshire fields and{" "}
            <strong>{midWales.trafficability}%</strong> of Mid-Wales fields.
          </li>
          <li>
            Slurry spreading window closed on{" "}
            <strong>{midWales.spreadingWindowClosedPct}%</strong> of high-risk Mid-Wales
            fields.
          </li>
        </ul>
      </div>

      <div className={styles.card} style={{ gridColumn: "span 6" }}>
        <div className={styles.cardHeader}>
          <div>
            <div className={styles.cardTitle}>Runoff and access indices</div>
            <div className={styles.cardSubtitle}>
              Daily runoff, heat stress, and field access overview
            </div>
          </div>
          <span className={`${styles.badge} ${styles.amber}`}>Runoff elevated</span>
        </div>
        <div className={styles.kpiRow}>
          <div className={styles.kpiBlock}>
            <div className={styles.kpiLabel}>Runoff Risk Index (0–1)</div>
            <div className={styles.kpiValue}>{runoffRiskIndex.toFixed(2)}</div>
            <div className={styles.kpiDelta}>
              High: {runoffHigh} farms; Medium: {runoffMedium} farms
            </div>
          </div>
          <div className={styles.kpiBlock}>
            <div className={styles.kpiLabel}>Field access</div>
            <div className={styles.kpiValue}>
              {fieldAccessPctGrass}% grass / {fieldAccessPctArable}% arable
            </div>
            <div className={styles.kpiDelta}>Machinery access remains constrained on wet clays.</div>
          </div>
        </div>
        <ul className={styles.bulletList}>
          <li>Heat stress risk: Low across the portfolio today.</li>
          <li>
            Recommend monitoring tyre rutting and compaction risk on heavier land before
            travel.
          </li>
        </ul>
      </div>
    </div>
  );
}

function Markets({ markets }) {
  const {
    beefPrice,
    beef7dAvg,
    lambPrice,
    lamb7dAvg,
    feedDeltaPct,
    fertiliserDeltaPct,
    energyDeltaPct,
    marginPressureIndex,
    highPressureFarms
  } = markets;

  return (
    <div className={styles.grid}>
      <div className={styles.card} style={{ gridColumn: "span 6" }}>
        <div className={styles.cardHeader}>
          <div>
            <div className={styles.cardTitle}>Livestock markets</div>
            <div className={styles.cardSubtitle}>
              Regional beef and lamb prices vs 7-day moving average
            </div>
          </div>
          <span className={styles.pillMuted}>Mock data for layout</span>
        </div>
        <div className={styles.kpiRow}>
          <div className={styles.kpiBlock}>
            <div className={styles.kpiLabel}>Beef (R4L, p/kg deadweight)</div>
            <div className={styles.kpiValue}>{(beefPrice * 100).toFixed(0)}</div>
            <div className={styles.kpiDelta}>
              7-day average: {(beef7dAvg * 100).toFixed(0)} p/kg
            </div>
          </div>
          <div className={styles.kpiBlock}>
            <div className={styles.kpiLabel}>Lamb (R3L, p/kg deadweight)</div>
            <div className={styles.kpiValue}>{(lambPrice * 100).toFixed(0)}</div>
            <div className={styles.kpiDelta}>
              7-day average: {(lamb7dAvg * 100).toFixed(0)} p/kg
            </div>
          </div>
        </div>
        <ul className={styles.bulletList}>
          <li>Price trend: slight upward movement for both beef and lamb.</li>
          <li>Short-term volatility remains within normal seasonal range.</li>
        </ul>
      </div>

      <div className={styles.card} style={{ gridColumn: "span 6" }}>
        <div className={styles.cardHeader}>
          <div>
            <div className={styles.cardTitle}>Input costs and margin pressure</div>
            <div className={styles.cardSubtitle}>
              Composite indicator based on feed, fertiliser, and energy
            </div>
          </div>
          <span className={`${styles.badge} ${styles.amber}`}>Moderate pressure</span>
        </div>
        <div className={styles.kpiRow}>
          <div className={styles.kpiBlock}>
            <div className={styles.kpiLabel}>Feed cost index vs 30-day avg</div>
            <div className={styles.kpiValue}>
              {feedDeltaPct > 0 ? "+" : ""}
              {feedDeltaPct.toFixed(1)}%
            </div>
          </div>
          <div className={styles.kpiBlock}>
            <div className={styles.kpiLabel}>Fertiliser index vs 30-day avg</div>
            <div className={styles.kpiValue}>
              {fertiliserDeltaPct > 0 ? "+" : ""}
              {fertiliserDeltaPct.toFixed(1)}%
            </div>
          </div>
          <div className={styles.kpiBlock}>
            <div className={styles.kpiLabel}>Energy index vs last month</div>
            <div className={styles.kpiValue}>
              {energyDeltaPct > 0 ? "+" : ""}
              {energyDeltaPct.toFixed(1)}%
            </div>
          </div>
        </div>
        <ul className={styles.bulletList}>
          <li>
            Margin Pressure Index: <strong>{marginPressureIndex.toFixed(2)}</strong> on a
            0–1 scale.
          </li>
          <li>
            High pressure flagged on <strong>{highPressureFarms}</strong> farms; monitor
            procurement scheduling for cashflow stress.
          </li>
        </ul>
      </div>
    </div>
  );
}

function DefraAlerts({ alerts }) {
  const { defra, schemeParticipation } = alerts;
  const { sfiFarms, prismFarms, compliantFarms, totalFarms } = schemeParticipation;

  return (
    <div className={styles.grid}>
      <div className={styles.card} style={{ gridColumn: "span 7" }}>
        <div className={styles.cardHeader}>
          <div>
            <div className={styles.cardTitle}>Defra and regulatory alerts</div>
            <div className={styles.cardSubtitle}>
              Filtered to ABP / Sainsbury's portfolio footprint
            </div>
          </div>
          <span className={styles.pillMuted}>Top 3 items for today</span>
        </div>
        <ul className={styles.bulletList}>
          {defra.map((item) => (
            <li key={item.id}>
              <strong>{item.title}</strong> ·{" "}
              <span className={styles.badge}>{item.type}</span> ·{" "}
              <span className={styles.badge}>
                {item.impact}
              </span>
              <div style={{ marginTop: 2, fontSize: "0.72rem", color: "#6c738a" }}>
                {item.note}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.card} style={{ gridColumn: "span 5" }}>
        <div className={styles.cardHeader}>
          <div>
            <div className={styles.cardTitle}>Scheme participation snapshot</div>
            <div className={styles.cardSubtitle}>
              SFI, PRISM / PRISM+, and internal standards
            </div>
          </div>
          <span className={`${styles.badge} ${styles.green}`}>Strategic opportunity</span>
        </div>
        <ul className={styles.bulletList}>
          <li>
            SFI participation: <strong>{sfiFarms}</strong> of <strong>{totalFarms}</strong>{" "}
            farms ({((sfiFarms / totalFarms) * 100).toFixed(0)}%).
          </li>
          <li>
            ABP PRISM / PRISM+ benchmarking: <strong>{prismFarms}</strong> farms (
            {((prismFarms / totalFarms) * 100).toFixed(0)}%).
          </li>
          <li>
            Farms meeting both Defra and internal nutrient standards:{" "}
            <strong>{compliantFarms}</strong> (
            {((compliantFarms / totalFarms) * 100).toFixed(0)}% of portfolio).
          </li>
        </ul>
        <div className={styles.chipRow}>
          <span className={styles.chip}>Candidate farms for SFI buffers</span>
          <span className={styles.chip}>Align PRISM metrics with FarmTwin MRV</span>
        </div>
      </div>
    </div>
  );
}

function DataQuality({ dataQuality }) {
  const { farmsSynced24h, farmsStale, streamsActive, streamsTotal, mrvConfidence } =
    dataQuality;

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>Data quality and MRV confidence</div>
          <div className={styles.cardSubtitle}>
            Indicates how much trust to place in today's portfolio view
          </div>
        </div>
        <span className={mrvConfidence > 0.8 ? `${styles.badge} ${styles.green}` : `${styles.badge} ${styles.amber}`}>
          MRV confidence {Math.round(mrvConfidence * 100)}%
        </span>
      </div>
      <div className={styles.kpiRow}>
        <div className={styles.kpiBlock}>
          <div className={styles.kpiLabel}>Farms synced in last 24 hours</div>
          <div className={styles.kpiValue}>
            {farmsSynced24h}/{mockPortfolio.farmsTotal}
          </div>
        </div>
        <div className={styles.kpiBlock}>
          <div className={styles.kpiLabel}>Farms with stale data (&gt; 72 hours)</div>
          <div className={styles.kpiValue}>{farmsStale}</div>
        </div>
        <div className={styles.kpiBlock}>
          <div className={styles.kpiLabel}>Sensor / data streams active</div>
          <div className={styles.kpiValue}>
            {streamsActive}/{streamsTotal}
          </div>
        </div>
      </div>
      <ul className={styles.bulletList}>
        <li>
          Two farms have prolonged gaps in slurry export documentation; flagged for follow-up
          this week.
        </li>
        <li>
          CAN / Black Box telematics online for <strong>{streamsActive}</strong> of{" "}
          <strong>{streamsTotal}</strong> units; remaining devices due firmware updates.
        </li>
      </ul>
    </div>
  );
}

function DailyActions({ actions }) {
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>Daily priority actions</div>
          <div className={styles.cardSubtitle}>
            Ranked by impact and urgency across ABP, Sainsbury's, and x10AI
          </div>
        </div>
        <span className={`${styles.badge} ${styles.amber}`}>7–10 key moves per day</span>
      </div>
      <ol className={styles.actionsList}>
        {actions.map((a) => (
          <li key={a.id}>
            <strong>{a.audience}</strong>: {a.text}
          </li>
        ))}
      </ol>
      <div className={styles.actionsTagline}>
        Use this list as the agenda for daily portfolio check-ins or joint ABP / Sainsbury's
        huddles.
      </div>
    </div>
  );
}

// Contract fulfilment forecast view
function ContractForecastView({ contract }) {
  const { horizons, weekly, drivers } = contract;

  function varianceStatusClass(variancePct) {
    const abs = Math.abs(variancePct);
    if (abs < 2) return styles.green;
    if (abs < 5) return styles.amber;
    return "red";
  }

  function renderHorizonCard(h) {
    const beefVar = h.beef.forecast - h.beef.contracted;
    const lambVar = h.lamb.forecast - h.lamb.contracted;
    const beefVarPct = (beefVar / h.beef.contracted) * 100;
    const lambVarPct = (lambVar / h.lamb.contracted) * 100;

    return (
      <div key={h.id} className={styles.card} style={{ gridColumn: "span 3" }}>
        <div className={styles.cardHeader}>
          <div>
            <div className={styles.cardTitle}>{h.label}</div>
            <div className={styles.cardSubtitle}>Contract vs forecast (head)</div>
          </div>
          <span className={`${styles.badge} ${varianceStatusClass(beefVarPct)}`}>
            Beef {beefVar >= 0 ? "+" : ""}
            {beefVar} ({beefVarPct.toFixed(1)}%)
          </span>
        </div>
        <div className={styles.kpiRow}>
          <div className={styles.kpiBlock}>
            <div className={styles.kpiLabel}>Beef (contracted → forecast)</div>
            <div className={styles.kpiValue}>
              {h.beef.contracted} → {h.beef.forecast}
            </div>
          </div>
          <div className={styles.kpiBlock}>
            <div className={styles.kpiLabel}>Lamb (contracted → forecast)</div>
            <div className={styles.kpiValue}>
              {h.lamb.contracted} → {h.lamb.forecast}
            </div>
          </div>
        </div>
        <ul className={styles.bulletList}>
          <li>
            Lamb variance:{" "}
            <strong className={`${styles.badge} ${varianceStatusClass(lambVarPct)}`}>
              {lambVar >= 0 ? "+" : ""}
              {lambVar} ({lambVarPct.toFixed(1)}%)
            </strong>
          </li>
          <li>{h.note}</li>
        </ul>
      </div>
    );
  }

  return (
    <>
      <section className={styles.screenOnly}>
        <div className={styles.sectionTitle}>Contract fulfilment forecast</div>
        <div className={styles.grid}>
          {horizons.map((h) => renderHorizonCard(h))}
        </div>
      </section>

      <section className={styles.screenOnly}>
        <div className={styles.sectionTitle}>Weekly supply vs Sainsbury's contract</div>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div>
              <div className={styles.cardTitle}>Next 4 weeks</div>
              <div className={styles.cardSubtitle}>
                Headcount comparison by week, beef and lamb combined
              </div>
            </div>
            <span className={styles.pillMuted}>Mock prediction shell</span>
          </div>
<table className={styles.table}>
  <thead>
    <tr className={styles.tr}>
      <th className={styles.th}>Week (start)</th>
      <th className={styles.th}>Beef contracted</th>
      <th className={styles.th}>Beef forecast</th>
      <th className={styles.th}>Lamb contracted</th>
      <th className={styles.th}>Lamb forecast</th>
      <th className={styles.th}>Comment</th>
    </tr>
  </thead>
  <tbody>
    {weekly.map((w) => (
      <tr key={w.weekStart} className={styles.tr}>
        <td className={styles.td}>{w.weekStart}</td>
        <td className={styles.td}>{w.beefContracted}</td>
        <td className={styles.td}>{w.beefForecast}</td>
        <td className={styles.td}>{w.lambContracted}</td>
        <td className={styles.td}>{w.lambForecast}</td>
        <td className={styles.td}>{w.comment}</td>
      </tr>
    ))}
  </tbody>
</table>

        </div>
      </section>

      <section className={styles.screenOnly}>
        <div className={styles.sectionTitle}>Key drivers and levers</div>
        <div className={styles.grid}>
          <div className={styles.card} style={{ gridColumn: "span 7" }}>
            <div className={styles.cardHeader}>
              <div>
                <div className={styles.cardTitle}>Drivers from FarmTwin daily view</div>
                <div className={styles.cardSubtitle}>
                  Conditions, DLWG and operational constraints behind the forecast
                </div>
              </div>
              <span className={styles.pillMuted}>Illustrative bullets</span>
            </div>
            <ul className={styles.bulletList}>
              {drivers.map((d, idx) => (
                <li key={idx}>{d}</li>
              ))}
            </ul>
          </div>
          <div className={styles.card} style={{ gridColumn: "span 5" }}>
            <div className={styles.cardHeader}>
              <div>
                <div className={styles.cardTitle}>Indicative actions</div>
                <div className={styles.cardSubtitle}>
                  How ABP and Sainsbury's may respond to shortfalls or surpluses
                </div>
              </div>
              <span className={`${styles.badge} ${styles.amber}`}>For discussion</span>
            </div>
            <ol className={styles.actionsList}>
              <li>
                <strong>ABP Procurement</strong>: Adjust kill mix in the next 7 days to absorb
                lamb surplus while protecting beef availability.
              </li>
              <li>
                <strong>Sainsbury's Sourcing</strong>: Consider flexing promotional activity
                between beef and lamb around weeks with forecast shortfalls.
              </li>
              <li>
                <strong>Farm Engagement</strong>: Work with three high-volume beef farms with
                below-plan DLWG to review diets and housing days.
              </li>
              <li>
                <strong>x10AI / Data</strong>: Prioritise liveweight data collection on farms
                where the 30–60 day forecast is most material to the contract.
              </li>
            </ol>
          </div>
        </div>
      </section>
    </>
  );
}

// PDF summary view (as previously)
function PDFView({ portfolio, prioritiesList }) {
  const { esg, weather, markets, alerts, dataQuality, dailyActions } = portfolio;
  const { E1, E2, E3, S4, G5 } = esg;
  const ghgImprovementPct = ((E2.baseline - E2.value) / E2.baseline) * 100;

  return (
    <div className={styles.pdfViewWrapper}>
      <header className={styles.pdfHeader}>
        <h1 className={styles.pdfTitle}>
          x10AI FarmTwin – ABP / Sainsbury's Daily Portfolio Summary
        </h1>
        <div className={styles.pdfMeta}>
          {portfolio.date} · Data as of {portfolio.asOf} · Farms reporting:{" "}
          {portfolio.farmsReporting}/{portfolio.farmsTotal}
        </div>
      </header>

      <div className={styles.pdfGrid}>
        <section className={styles.pdfSection}>
          <div className={styles.pdfSectionTitle}>ESG snapshot (E1–E3, S4, G5)</div>
          <div className={styles.pdfKpiRow}>
            <div className={styles.pdfKpi}>
              <div className={styles.pdfKpiLabel}>E1 – Nitrogen Efficiency Index</div>
              <div className={styles.pdfKpiValue}>
                {E1.score} ({E1.delta >= 0 ? "+" : ""}
                {E1.delta} vs 30-day avg)
              </div>
            </div>
            <div className={styles.pdfKpi}>
              <div className={styles.pdfKpiLabel}>E2 – GHG Intensity (kg CO₂e / kg LW)</div>
              <div className={styles.pdfKpiValue}>
                {E2.value.toFixed(1)} ({ghgImprovementPct.toFixed(1)}% below baseline)
              </div>
            </div>
          </div>
          <ul className={styles.pdfList}>
            <li>
              E3 – Manure / runoff: {E3.highRiskFields} of {E3.totalFields} fields flagged
              as High risk; slurry storage under 10 weeks on {E3.slurryLowWeeks} farms.
            </li>
            <li>
              S4 – Soil and land: {S4.soilTestsUpToDatePct}% up-to-date soil tests;{" "}
              {S4.highPFieldsPct}% fields at P Index +3 or higher; riparian buffers on{" "}
              {S4.riparianBuffersPct}% of at-risk edges.
            </li>
            <li>
              G5 – Governance: {G5.farmsCompleteData}/{G5.farmsTotal} farms with complete
              data; {G5.verifiedPlans}/{G5.farmsTotal} verified nutrient plans; MRV coverage{" "}
              {G5.mrvCoveragePct}%.
            </li>
          </ul>
        </section>

        <section className={styles.pdfSection}>
          <div className={styles.pdfSectionTitle}>Local conditions and risk indices</div>
          <div className={styles.pdfKpiRow}>
            <div className={styles.pdfKpi}>
              <div className={styles.pdfKpiLabel}>Shropshire</div>
              <div className={styles.pdfKpiValue}>{weather.shropshire.today}</div>
            </div>
            <div className={styles.pdfKpi}>
              <div className={styles.pdfKpiLabel}>Mid-Wales</div>
              <div className={styles.pdfKpiValue}>{weather.midWales.today}</div>
            </div>
          </div>
          <ul className={styles.pdfList}>
            <li>
              7-day rainfall: {weather.shropshire.rainfall7d} mm (Shropshire),{" "}
              {weather.midWales.rainfall7d} mm (Mid-Wales); trafficability restricted on{" "}
              {weather.shropshire.trafficability}% and {weather.midWales.trafficability}%
              of fields respectively.
            </li>
            <li>
              Runoff Risk Index: {weather.runoffRiskIndex.toFixed(2)} (High on{" "}
              {weather.runoffHigh} farms; Medium on {weather.runoffMedium} farms).
            </li>
            <li>
              Field access: {weather.fieldAccessPctGrass}% grassland and{" "}
              {weather.fieldAccessPctArable}% arable currently accessible for machinery;
              heat stress risk low across portfolio.
            </li>
          </ul>
        </section>

        <section className={styles.pdfSection}>
          <div className={styles.pdfSectionTitle}>Markets and margin context</div>
          <div className={styles.pdfKpiRow}>
            <div className={styles.pdfKpi}>
              <div className={styles.pdfKpiLabel}>Beef (R4L, p/kg deadweight)</div>
              <div className={styles.pdfKpiValue}>
                {(markets.beefPrice * 100).toFixed(0)} (7-day avg{" "}
                {(markets.beef7dAvg * 100).toFixed(0)})
              </div>
            </div>
            <div className={styles.pdfKpi}>
              <div className={styles.pdfKpiLabel}>Lamb (R3L, p/kg deadweight)</div>
              <div className={styles.pdfKpiValue}>
                {(markets.lambPrice * 100).toFixed(0)} (7-day avg{" "}
                {(markets.lamb7dAvg * 100).toFixed(0)})
              </div>
            </div>
          </div>
          <ul className={styles.pdfList}>
            <li>
              Input indices vs 30-day average: feed{" "}
              {markets.feedDeltaPct > 0 ? "+" : ""}
              {markets.feedDeltaPct.toFixed(1)}%, fertiliser{" "}
              {markets.fertiliserDeltaPct > 0 ? "+" : ""}
              {markets.fertiliserDeltaPct.toFixed(1)}%, energy{" "}
              {markets.energyDeltaPct > 0 ? "+" : ""}
              {markets.energyDeltaPct.toFixed(1)}% vs last month.
            </li>
            <li>
              Margin Pressure Index: {markets.marginPressureIndex.toFixed(2)} (0–1 scale);
              High pressure flagged on {markets.highPressureFarms} farms.
            </li>
          </ul>
        </section>

        <section className={styles.pdfSection}>
          <div className={styles.pdfSectionTitle}>Regulatory signals and scheme participation</div>
          <ul className={styles.pdfList}>
            {alerts.defra.map((item) => (
              <li key={item.id}>
                <strong>{item.title}</strong> ({item.type}, {item.impact}). {item.note}
              </li>
            ))}
            <li>
              SFI participation: {alerts.schemeParticipation.sfiFarms}/
              {alerts.schemeParticipation.totalFarms} farms; PRISM / PRISM+ on{" "}
              {alerts.schemeParticipation.prismFarms} farms;{" "}
              {alerts.schemeParticipation.compliantFarms} farms meeting both Defra and
              internal nutrient standards.
            </li>
          </ul>
        </section>
      </div>

      <section className={`${styles.pdfSection} ${styles.pdfActions}`} style={{ marginTop: 8 }}>
        <div className={styles.pdfActionsTitle}>Top priorities for today</div>
        <ol className={styles.pdfActionsList}>
          {dailyActions.map((a) => (
            <li key={a.id}>
              <strong>{a.audience}</strong>: {a.text}
            </li>
          ))}
        </ol>
      </section>

      <section className={styles.pdfSection} style={{ marginTop: 8 }}>
        <div className={styles.pdfSectionTitle}>Headline risk and opportunity signals</div>
        <ul className={styles.pdfList}>
          {prioritiesList.map((p) => (
            <li key={p.id}>{p.text}</li>
          ))}
        </ul>
      </section>

      <div className={styles.pdfFooterNote}>
        Generated from x10AI FarmTwin mock data shell. Replace with live FarmTwin and external
        feeds for production use.
      </div>
    </div>
  );
}

export default function AbpSainsburysDashboard() {
  const { filters } = mockPortfolio;
  const [mode, setMode] = React.useState("dashboard"); // "dashboard" | "pdf" | "contract"

  return (
    <div className={styles.appRoot}>
        <div className={styles.appShell}>
        <header className={`${styles.appHeader} ${styles.screenOnly}`}>
            <div className={styles.appTitleGroup}>
            <h1>x10AI FarmTwin – ABP / Sainsbury's Daily Views</h1>
            <p>
                {mockPortfolio.date} · Data as of {mockPortfolio.asOf} · Farms reporting:{" "}
                <strong>
                {mockPortfolio.farmsReporting}/{mockPortfolio.farmsTotal}
                </strong>
            </p>
            <div style={{ marginTop: 6 }}>
                {mockPortfolio.tags.map((t) => (
                <span key={t} className={styles.tagPill}>
                    {t}
                </span>
                ))}
            </div>
            </div>
            <div>
            <div className={styles.filtersRow}>
                <span className={`${styles.filterChip} ${styles.filterChipActive}`}>Retailer: {filters.retailer}</span>
                <span className={styles.filterChip}>Species: {filters.species}</span>
                <span className={styles.filterChip}>Region: {filters.region}</span>
                <span className={styles.filterChip}>Scheme: {filters.scheme}</span>
            </div>
            <div className={styles.metaRow}>
                <span>
                Engine: <strong>x10AI FarmTwin</strong>
                </span>
                <span>Views: Portfolio, PDF summary, Contract fulfilment forecast</span>
            </div>
            <div className={styles.modeToggleRow}>
                <button
                className={`${styles.modeButton} ${mode === "dashboard" ? styles.active : ""}`}
                onClick={() => setMode("dashboard")}
                >
                Portfolio dashboard
                </button>
                <button
                className={`${styles.modeButton} ${mode === "pdf" ? styles.active : ""}`}
                onClick={() => setMode("pdf")}
                >
                PDF summary view
                </button>
                <button
                className={`${styles.modeButton} ${mode === "contract" ? styles.active : ""}`}
                onClick={() => setMode("contract")}
                >
                Contract fulfilment forecast
                </button>
            </div>
            </div>
        </header>

        {mode === "dashboard" && (
            <>
            <PriorityStrip />

            <section className={styles.screenOnly}>
                <div className={styles.sectionTitle}>Portfolio ESG snapshot</div>
                <ESGSnapshot esg={mockPortfolio.esg} />
            </section>

            <section className={styles.screenOnly}>
                <div className={styles.sectionTitle}>Local conditions and risk indices</div>
                <WeatherAndRisk weather={mockPortfolio.weather} />
            </section>

            <section className={styles.screenOnly}>
                <div className={styles.sectionTitle}>Markets and margin context</div>
                <Markets markets={mockPortfolio.markets} />
            </section>

            <section className={styles.screenOnly}>
                <div className={styles.sectionTitle}>Regulatory signals and scheme participation</div>
                <DefraAlerts alerts={mockPortfolio.alerts} />
            </section>

            <section className={styles.screenOnly}>
                <div className={styles.sectionTitle}>Data confidence and actions</div>
                <div className={styles.grid}>
                <div style={{ gridColumn: "span 7" }}>
                    <DataQuality dataQuality={mockPortfolio.dataQuality} />
                </div>
                <div style={{ gridColumn: "span 5" }}>
                    <DailyActions actions={mockPortfolio.dailyActions} />
                </div>
                </div>
            </section>

            <div className={`${styles.footerNote} ${styles.screenOnly}`}>
                Layout with mock data only. Hook up to live FarmTwin and external feeds for production
                use.
            </div>
            </>
        )}

        {mode === "pdf" && (
            <section>
            <PDFView portfolio={mockPortfolio} prioritiesList={priorities} />
            </section>
        )}

        {mode === "contract" && (
            <section>
            <ContractForecastView contract={mockContract} />
            </section>
        )}
        </div>
    </div>
  );
}
